import { clamp, LANDMARKS } from './helpers'
import { FACE_MESH_CONTOUR_PATHS } from './constants'

const LIP_OUTER_INDICES = Array.from(
  new Set((FACE_MESH_CONTOUR_PATHS?.lipsOuter || []).filter((idx) => Number.isInteger(idx)))
)
const LIP_INNER_INDICES = Array.from(
  new Set((FACE_MESH_CONTOUR_PATHS?.lipsInner || []).filter((idx) => Number.isInteger(idx)))
)
const LIP_INNER_SET = new Set(LIP_INNER_INDICES)
const LIP_INDICES = Array.from(
  new Set(
    [...LIP_OUTER_INDICES, ...LIP_INNER_INDICES].filter((idx) => Number.isInteger(idx))
  )
)
const LIP_SET = new Set(LIP_INDICES)

const JAW_SUPPORT_INDICES = Array.from(
  new Set((FACE_MESH_CONTOUR_PATHS?.faceOval || []).filter((idx) => Number.isInteger(idx)))
)

const readSetting = (value, fallback, min, max) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  return clamp(n, min, max)
}

const getRigCanonicalPoint = (avatarRig, idx) => {
  const p = avatarRig?.canonical?.[idx]
  if (!p) return null
  return p
}

export const computeJawByMouthRatio = (liveCanonical) => {
  if (!liveCanonical?.length) return 0
  const mouthUpper = liveCanonical[LANDMARKS.mouthUpper]
  const mouthLower = liveCanonical[LANDMARKS.mouthLower]
  const mouthLeft = liveCanonical[LANDMARKS.mouthLeft]
  const mouthRight = liveCanonical[LANDMARKS.mouthRight]
  if (!mouthUpper || !mouthLower || !mouthLeft || !mouthRight) return 0
  const mouthOpen = Math.abs(mouthLower.y - mouthUpper.y)
  const mouthWidth = Math.max(1e-4, Math.abs(mouthRight.x - mouthLeft.x))
  return clamp((mouthOpen / mouthWidth - 0.16) / 0.22, 0, 1)
}

export const resolveVisualJawOpen = ({
  facialJawOpen = 0,
  liveCanonical = null,
  blendshapeSignals = null
} = {}) => {
  let jaw = clamp(Number(facialJawOpen) || 0, 0, 1)
  jaw = Math.max(jaw, computeJawByMouthRatio(liveCanonical))
  const bsConfidence = clamp(Number(blendshapeSignals?.confidence) || 0, 0, 1)
  if (bsConfidence > 0.04) {
    const bsJaw = clamp(Number(blendshapeSignals?.jawOpen) || 0, 0, 1)
    const bsMouthOpen = clamp(Number(blendshapeSignals?.mouthOpen) || 0, 0, 1)
    jaw = Math.max(jaw, bsJaw, bsMouthOpen * 0.92)
  }
  return clamp(jaw, 0, 1)
}

export const mergeJawWithAudio = ({
  visualJawOpen = 0,
  mouthLevel = 0,
  useAudioMouth = false,
  audioMouthFallback = false
} = {}) => {
  const visualJaw = clamp(Number(visualJawOpen) || 0, 0, 1)
  if (!useAudioMouth && !audioMouthFallback) return visualJaw
  const audioJaw = clamp((Number(mouthLevel) || 0) * 1.05, 0, 1)
  if (visualJaw > 0.05) {
    return Math.max(visualJaw, useAudioMouth ? audioJaw : audioJaw * 0.8)
  }
  return audioJaw
}

export const computeMouthDrive = ({
  jawOpen = 0,
  lipSyncVolume = 0,
  animateMouth = true,
  analyser = null,
  analyserData = null
} = {}) => {
  let drive = Math.max(clamp(Number(lipSyncVolume) || 0, 0, 1), clamp(Number(jawOpen) || 0, 0, 1))
  if (drive > 0.02 || !animateMouth || !analyser || !analyserData?.length) return drive
  try {
    analyser.getByteTimeDomainData(analyserData)
    let sum = 0
    for (let i = 0; i < analyserData.length; i += 1) {
      const v = (analyserData[i] - 128) / 128
      sum += v * v
    }
    const rms = Math.sqrt(sum / analyserData.length)
    drive = Math.max(drive, Math.min(1, rms * 4))
  } catch {}
  return clamp(drive, 0, 1)
}

export const applyCanonicalMouthDeformation = ({
  points,
  avatarRig,
  settings = {},
  jawOpen = 0,
  mouthLevel = 0,
  blendshapeSignals = null,
  liveCanonical = null,
  baselineCanonical = null
} = {}) => {
  if (!points?.length || !avatarRig?.mouthCenter || !LIP_INDICES.length) return

  const center = avatarRig.mouthCenter
  const mouthRadiusX = Number(avatarRig.mouthRadiusX) || 0
  const mouthRadiusY = Number(avatarRig.mouthRadiusY) || 0
  if (!(mouthRadiusX > 0) || !(mouthRadiusY > 0)) return
  const mouthSpanY = Math.max(mouthRadiusY, mouthRadiusX * 0.24)

  const bsConfidence = clamp(Number(blendshapeSignals?.confidence) || 0, 0, 1)
  const bsJaw = clamp(Number(blendshapeSignals?.jawOpen) || 0, 0, 1)
  const bsMouthOpen = clamp(Number(blendshapeSignals?.mouthOpen) || 0, 0, 1)
  const bsSmileL = clamp(Number(blendshapeSignals?.mouthSmileLeft) || 0, 0, 1)
  const bsSmileR = clamp(Number(blendshapeSignals?.mouthSmileRight) || 0, 0, 1)
  const bsPucker = clamp(Number(blendshapeSignals?.mouthPucker) || 0, 0, 1)
  const bsFunnel = clamp(Number(blendshapeSignals?.mouthFunnel) || 0, 0, 1)

  const blendedJaw = Math.max(
    clamp(Number(jawOpen) || 0, 0, 1),
    bsConfidence > 0.04 ? Math.max(bsJaw, bsMouthOpen * 0.92) : 0
  )
  const audioFallbackDrive = clamp((Number(mouthLevel) || 0) * 0.4, 0, 1)
  const openDrive = clamp(
    blendedJaw > 0.08 ? blendedJaw : Math.max(blendedJaw, audioFallbackDrive),
    0,
    1
  )
  const openGain = readSetting(settings.mouthOpenGain, 0.65, 0, 2)
  const closeGain = readSetting(settings.mouthCloseGain, 0.5, 0, 2)
  const regionX = readSetting(settings.mouthRegionX, 0.9, 0.4, 1.4)
  const rx = Math.max(1e-4, mouthRadiusX * regionX)

  const openAmount = Math.pow(openDrive, 0.95)
  const closeAmount = Math.pow(clamp((0.2 - openDrive) / 0.2, 0, 1), 1.15)
  const outerUpperShift = mouthSpanY * openAmount * (0.05 + openGain * 0.08)
  const outerLowerShift = mouthSpanY * openAmount * (0.02 + openGain * 0.04)
  const innerUpperShift = mouthSpanY * openAmount * (0.08 + openGain * 0.09)
  const innerLowerShift = mouthSpanY * openAmount * (0.03 + openGain * 0.05)
  const closeShift = mouthSpanY * closeAmount * closeGain * 0.04

  const smileDrive = Math.max(bsSmileL, bsSmileR)
  const puckerDrive = Math.max(bsPucker, bsFunnel)
  const widthScaleTarget = clamp(1 + smileDrive * 0.04 - puckerDrive * 0.06, 0.94, 1.06)

  const canRetargetFromCamera =
    !!avatarRig?.canonical?.length &&
    !!liveCanonical?.length &&
    !!baselineCanonical?.length
  if (canRetargetFromCamera) {
    const upperLive = liveCanonical[LANDMARKS.mouthUpper]
    const upperBase = baselineCanonical[LANDMARKS.mouthUpper]
    const lowerLive = liveCanonical[LANDMARKS.mouthLower]
    const lowerBase = baselineCanonical[LANDMARKS.mouthLower]
    const leftLive = liveCanonical[LANDMARKS.mouthLeft]
    const leftBase = baselineCanonical[LANDMARKS.mouthLeft]
    const rightLive = liveCanonical[LANDMARKS.mouthRight]
    const rightBase = baselineCanonical[LANDMARKS.mouthRight]

    if (upperLive && upperBase && lowerLive && lowerBase && leftLive && leftBase && rightLive && rightBase) {
      const maxDx = mouthRadiusX * 0.78
      const maxDy = mouthSpanY * 1.45
      const retargetStrength = readSetting(settings.mouthRetargetStrength, 0.64, 0, 1.2)
      const retargetXGain = readSetting(settings.mouthRetargetXGain, 0.34, 0, 2.4)
      const retargetYGain = readSetting(settings.mouthRetargetYGain, 0.74, 0, 2.8)

      const upperDy = clamp((upperLive.y - upperBase.y) * retargetYGain, -mouthSpanY * 0.55, mouthSpanY * 1.1)
      const lowerDy = clamp((lowerLive.y - lowerBase.y) * retargetYGain, -mouthSpanY * 0.35, mouthSpanY * 1.6)
      const leftDx = clamp((leftLive.x - leftBase.x) * retargetXGain, -maxDx, maxDx)
      const rightDx = clamp((rightLive.x - rightBase.x) * retargetXGain, -maxDx, maxDx)
      const openAnchor = Math.max(0, lowerDy - upperDy)

      for (let i = 0; i < LIP_INDICES.length; i += 1) {
        const idx = LIP_INDICES[i]
        if (idx < 0 || idx >= points.length) continue
        if (idx >= avatarRig.canonical.length) continue
        const p = points[idx]
        const rigP = avatarRig.canonical[idx]
        if (!p || !rigP) continue

        const isUpper = rigP.y < center.y
        const isInner = LIP_INNER_SET.has(idx)
        const side = rigP.x < center.x ? -1 : 1
        const cornerWeight = clamp(Math.abs(rigP.x - center.x) / Math.max(1e-4, rx), 0, 1)
        const centerWeight = 1 - cornerWeight

        const sideDx = side < 0 ? leftDx : rightDx
        const horizTarget = sideDx * (0.2 + cornerWeight * 0.8) * (isInner ? 0.65 : 0.86)
        const vertBase = isUpper ? upperDy : lowerDy
        const vertTarget = vertBase * (isInner ? 0.95 : 0.84)
        const openSplit = isInner
          ? (isUpper ? -openAnchor * 0.16 * centerWeight : openAnchor * 0.18 * centerWeight)
          : 0

        const targetX = rigP.x + horizTarget
        const targetY = rigP.y + vertTarget + openSplit
        const blend = clamp(
          retargetStrength * (0.55 + openAmount * 0.22) * (isInner ? 0.88 : 0.8),
          0,
          0.74
        )

        p.x = p.x * (1 - blend) + targetX * blend
        p.y = p.y * (1 - blend) + targetY * blend
      }

      const lipDrivenJaw = clamp(openAnchor / Math.max(1e-4, mouthSpanY * 0.86), 0, 1)
      const jawSupportDrive = Math.max(openDrive, lipDrivenJaw)
      const jawDrop = mouthSpanY * Math.pow(jawSupportDrive, 0.9) * (0.9 + openGain * 0.22)

      if (jawDrop > 1e-4 && avatarRig?.canonical?.length) {
        const count = Math.min(points.length, avatarRig.canonical.length)
        for (let idx = 0; idx < count; idx += 1) {
          const p = points[idx]
          const rigP = avatarRig.canonical[idx]
          if (!p || !rigP) continue
          if (rigP.y < center.y - mouthSpanY * 0.02) continue

          const dx = rigP.x - center.x
          const dy = rigP.y - center.y
          const horiz = clamp(1 - Math.abs(dx) / Math.max(1e-4, rx * 3.05), 0, 1)
          const vertical = clamp((dy + mouthSpanY * 0.06) / Math.max(1e-4, mouthSpanY * 2.35), 0, 1)
          const lowerBias = clamp((dy - mouthSpanY * 0.02) / Math.max(1e-4, mouthSpanY * 1.9), 0, 1)
          let influence = horiz * (vertical * 0.56 + lowerBias * 0.44)
          if (influence <= 1e-4) continue
          if (LIP_SET.has(idx)) influence *= 0.18
          p.y += jawDrop * influence
        }
      }

      const chinPoint = points[LANDMARKS.chin]
      if (chinPoint) chinPoint.y += jawDrop * 0.03
    }
    return
  }

  const applyLipGroup = (indices, upperShift, lowerShift, widthMin, widthMax, lowerCompression) => {
    for (let i = 0; i < indices.length; i += 1) {
      const idx = indices[i]
      if (idx < 0 || idx >= points.length) continue
      const p = points[idx]
      if (!p) continue
      const rigP = getRigCanonicalPoint(avatarRig, idx)
      const baseX = Number.isFinite(Number(rigP?.x)) ? rigP.x : p.x
      const baseY = Number.isFinite(Number(rigP?.y)) ? rigP.y : p.y
      const dx = p.x - center.x
      const horizInfluence = clamp(1 - Math.abs(dx) / Math.max(1e-4, rx * 1.45), 0, 1)
      const isUpper = baseY < center.y
      const openShift = (isUpper ? -upperShift : lowerShift) * horizInfluence
      // Never push lower lip upward in close phase; that causes "floating lip" artifacts.
      const closeShiftSigned = isUpper ? closeShift * horizInfluence : 0
      p.y += openShift + closeShiftSigned

      const cornerWeight = clamp(Math.abs(baseX - center.x) / Math.max(1e-4, rx), 0, 1)
      const widthBlend = widthMin + (widthMax - widthMin) * cornerWeight
      let widthScale = 1 + (widthScaleTarget - 1) * widthBlend
      if (!isUpper && lowerCompression > 0) {
        widthScale *= 1 - lowerCompression * openAmount * (1 - cornerWeight)
        widthScale = 1 + (widthScale - 1) * 0.35
      }
      p.x = center.x + dx * widthScale
    }
  }

  applyLipGroup(LIP_OUTER_INDICES, outerUpperShift, outerLowerShift, 0.25, 0.9, 0.06)
  applyLipGroup(LIP_INNER_INDICES, innerUpperShift, innerLowerShift, 0.2, 0.78, 0.08)

  // Lower-face support: move jawline/chin downward so jaw opening reads as head motion.
  const jawDrop = mouthSpanY * openAmount * (0.72 + openGain * 0.28)
  const lowerLipCarry = jawDrop * 0.52
  if (lowerLipCarry > 1e-4) {
    for (let i = 0; i < LIP_INDICES.length; i += 1) {
      const idx = LIP_INDICES[i]
      if (idx < 0 || idx >= points.length) continue
      const p = points[idx]
      if (!p) continue
      const rigP = getRigCanonicalPoint(avatarRig, idx)
      const baseY = Number.isFinite(Number(rigP?.y)) ? rigP.y : p.y
      if (baseY < center.y) continue
      const dx = p.x - center.x
      const horiz = clamp(1 - Math.abs(dx) / Math.max(1e-4, rx * 1.55), 0, 1)
      if (horiz > 1e-4) p.y += lowerLipCarry * horiz
    }
  }

  // Extra inner-lower-mouth opening so lip stays attached to jaw while the oral cavity opens.
  const lowerInnerOpen = jawDrop * 0.2
  if (lowerInnerOpen > 1e-4) {
    for (let i = 0; i < LIP_INNER_INDICES.length; i += 1) {
      const idx = LIP_INNER_INDICES[i]
      if (idx < 0 || idx >= points.length) continue
      const p = points[idx]
      if (!p) continue
      const rigP = getRigCanonicalPoint(avatarRig, idx)
      const baseY = Number.isFinite(Number(rigP?.y)) ? rigP.y : p.y
      if (baseY < center.y) continue
      const dx = p.x - center.x
      const horiz = clamp(1 - Math.abs(dx) / Math.max(1e-4, rx * 1.45), 0, 1)
      if (horiz > 1e-4) p.y += lowerInnerOpen * horiz
    }
  }

  // Keep central lower lip from protruding upward as jaw opens.
  const lowerLipAssist = jawDrop * 0.08
  if (lowerLipAssist > 1e-4) {
    for (let i = 0; i < LIP_INDICES.length; i += 1) {
      const idx = LIP_INDICES[i]
      if (idx < 0 || idx >= points.length) continue
      const p = points[idx]
      if (!p) continue
      const rigP = getRigCanonicalPoint(avatarRig, idx)
      const baseY = Number.isFinite(Number(rigP?.y)) ? rigP.y : p.y
      if (baseY < center.y) continue
      const dx = p.x - center.x
      const horiz = clamp(1 - Math.abs(dx) / Math.max(1e-4, rx * 1.65), 0, 1)
      if (horiz > 1e-4) p.y += lowerLipAssist * horiz
    }
  }

  if (jawDrop > 1e-4 && JAW_SUPPORT_INDICES.length) {
    for (let i = 0; i < JAW_SUPPORT_INDICES.length; i += 1) {
      const idx = JAW_SUPPORT_INDICES[i]
      if (idx < 0 || idx >= points.length) continue
      const p = points[idx]
      if (!p) continue
      const dx = p.x - center.x
      const dy = p.y - center.y
      if (dy < -mouthSpanY * 0.18) continue
      const horiz = clamp(1 - Math.abs(dx) / Math.max(1e-4, rx * 2.6), 0, 1)
      const vertical = clamp((dy + mouthSpanY * 0.15) / Math.max(1e-4, mouthSpanY * 2.6), 0, 1)
      const chinBias = clamp((dy - mouthSpanY * 0.28) / Math.max(1e-4, mouthSpanY * 2.0), 0, 1)
      const influence = horiz * (vertical * 0.64 + chinBias * 0.36)
      if (influence > 1e-4) p.y += jawDrop * influence
    }
  }

  const chinPoint = points[LANDMARKS.chin]
  if (chinPoint) chinPoint.y += jawDrop * 0.34
}

export default {
  computeJawByMouthRatio,
  resolveVisualJawOpen,
  mergeJawWithAudio,
  computeMouthDrive,
  applyCanonicalMouthDeformation
}
